<template>
    <div>
        <div v-if="isLoading" class="text-center">
            <span class="spinner-border spinner-border" role="status" aria-hidden="true"></span>
        </div>

        <div v-if="isError" class="alert alert-danger mb-3" role="alert">
            {{ $t('checkout.shipping_methods_error') }}
            <button class="btn btn-sm btn-outline-danger ms-2" @click="init">
                {{ $t('checkout.retry') }}
            </button>
        </div>

        <div
            class="form-check"
            v-if="!isLoading && !isError"
            v-for="shippingMethod in shippingMethods"
            :key="shippingMethod.id"
        >
            <input
                class="form-check-input"
                type="radio"
                name="delivery"
                :id="shippingMethod.id"
                v-model="selectedShippingMethodId"
                :value="shippingMethod.id"
                @change="updateShippingMethod(shippingMethod.id)"
                :disabled="isUpdatingShippingMethod"
            />
            <label class="form-check-label" :for="shippingMethod.id">
                <div class="d-flex justify-content-between align-items-center w-100">
                    <div>
                        <strong>{{ shippingMethod.translated?.name }}</strong>
                        <p class="small mb-0" v-if="shippingMethod.translated?.description">
                            {{ shippingMethod.translated?.description }}
                        </p>
                    </div>
                    <div v-if="getShippingPrice(shippingMethod)" class="shipping-price">
                        <span>&euro;{{ formatNumber(getShippingPrice(shippingMethod)) }}</span>
                    </div>
                    <div v-else class="shipping-price free">
                        {{ $t('checkout.free_shipping') }}
                    </div>
                </div>
            </label>
        </div>

        <div v-if="isNoDealerSelected && hasDealerOption" class="alert alert-info mt-3 small">
            {{ $t('checkout.select_dealer_notice') }}
        </div>
    </div>
</template>

<script>
import shopware from '../../mixins/shopware';
import formatters from '../../mixins/formatters';
import { EventBus } from '../../api/eventBus.ts';

export default {
    mixins: [shopware, formatters],
    props: {
        cart: Object|null,
        dealerSelectionUrl: {
            type: String,
            default: null
        }
    },
    data() {
        return {
            shippingMethods: [],
            isLoading: false,
            isError: false,
            isUpdatingShippingMethod: false,
            selectedShippingMethodId: null,
            internalCart: null,
            retryCount: 0,
            maxRetries: 3
        };
    },
    computed: {
        cartData() {
            return this.cart || this.internalCart;
        },
        hasDealerOption() {
            return this.shippingMethods.some(method => method.technicalName === 'shipping_dealer');
        },
        isNoDealerSelected() {
            return this.selectedShippingMethodId &&
                this.hasDealerOption &&
                this.shippingMethods.find(m => m.id === this.selectedShippingMethodId)?.technicalName === 'shipping_dealer' &&
                (!this.cartData?.extensions?.dealer);
        }
    },
    watch: {
        'cart.price.totalPrice': function(newVal) {
            this.init();
        },
        'cart': {
            handler: function(newCart) {
                if (newCart) {
                    this.internalCart = newCart;
                }
            },
            deep: true
        }
    },
    created: function () {
        this.init();
    },
    methods: {
        async init() {
            this.isLoading = true;
            this.isError = false;

            try {
                if (!this.cart) {
                    await this.fetchCart();
                }

                await this.getShippingMethods();

                // Set the default shipping method based on the cart data, otherwise default to dealer pickup or the first available one
                if (this.cartData && this.cartData.deliveries && this.cartData.deliveries[0]) {
                    const cartShippingMethod = this.cartData.deliveries[0].shippingMethod.id;

                    if (this.shippingMethods.find((method) => method.id === cartShippingMethod)) {
                        this.selectedShippingMethodId = cartShippingMethod;
                    } else if (this.shippingMethods.length > 0) {
                        this.selectedShippingMethodId = this.shippingMethods[0].id;
                        await this.updateShippingMethod(this.selectedShippingMethodId);
                    }
                } else if (this.shippingMethods.length > 0) {
                    this.selectedShippingMethodId = this.shippingMethods[0].id;
                    await this.updateShippingMethod(this.selectedShippingMethodId);
                }
            } catch (error) {
                console.error("Error initializing shipping methods:", error);

                // Retry logic for network errors
                if (this.retryCount < this.maxRetries) {
                    this.retryCount++;
                    console.log(`Retrying shipping methods init (${this.retryCount}/${this.maxRetries})...`);
                    await new Promise(resolve => setTimeout(resolve, 1000)); // Wait before retry
                    await this.init();
                } else {
                    this.isError = true;
                    this.retryCount = 0;
                }
            } finally {
                this.isLoading = false;
            }
        },

        async fetchCart() {
            try {
                this.internalCart = await this.api.client.invoke("readCart get /checkout/cart");
            } catch (error) {
                console.error("Error fetching cart:", error);
                throw error;
            }
        },

        async getShippingMethods() {
            try {
                const shippingMethods = await this.api.client.invoke("readShippingMethod post /shipping-method?onlyAvailable", {
                    onlyAvailable: true
                });

                if (!shippingMethods || !shippingMethods.elements) {
                    throw new Error("No shipping methods returned");
                }

                this.shippingMethods = shippingMethods.elements;

                // Sort methods - free shipping first, then by price
                this.sortShippingMethods();
            } catch (error) {
                console.error("Error fetching shipping methods:", error);
                throw error;
            }
        },

        sortShippingMethods() {
            this.shippingMethods.sort((a, b) => {
                const priceA = this.getShippingPrice(a) || 0;
                const priceB = this.getShippingPrice(b) || 0;
                return priceA - priceB;
            });
        },

        getShippingPrice(shippingMethod) {
            if (!shippingMethod || !shippingMethod.prices || !shippingMethod.prices.length) {
                return 0;
            }

            // Find the appropriate price based on cart total if relevant
            if (this.cartData && this.cartData.price) {
                const cartTotal = this.cartData.price.totalPrice;

                // Find the price that matches our cart total
                const applicablePrice = shippingMethod.prices.find(price => {
                    if (price.currencyPrice && price.currencyPrice.length > 0) {
                        const currencyPrice = price.currencyPrice[0];
                        return (!price.calculation || price.calculation === 'fixed') &&
                            (!currencyPrice.quantityStart || cartTotal >= currencyPrice.quantityStart) &&
                            (!currencyPrice.quantityEnd || cartTotal <= currencyPrice.quantityEnd);
                    }
                    return false;
                });

                if (applicablePrice && applicablePrice.currencyPrice && applicablePrice.currencyPrice.length > 0) {
                    return applicablePrice.currencyPrice[0].gross;
                }
            }

            // Default: return first price in list
            if (shippingMethod.prices && shippingMethod.prices.length > 0 &&
                shippingMethod.prices[0].currencyPrice &&
                shippingMethod.prices[0].currencyPrice.length > 0) {
                return shippingMethod.prices[0].currencyPrice[0].gross;
            }

            return 0;
        },

        async updateShippingMethod(shippingMethodId) {
            if (!shippingMethodId || this.isUpdatingShippingMethod) {
                return;
            }

            try {
                this.isUpdatingShippingMethod = true;
                EventBus.$emit('updating-shipping-method', true);

                await this.api.client.invoke("updateContext patch /context", {
                    shippingMethodId: shippingMethodId,
                });

                // If selecting dealer shipping but no dealer is selected, inform the user
                const selectedMethod = this.shippingMethods.find(m => m.id === shippingMethodId);
                if (selectedMethod && selectedMethod.technicalName === 'shipping_dealer' &&
                    (!this.cartData || !this.cartData.extensions || !this.cartData.extensions.dealer)) {
                    // Redirect to dealer selection if URL is provided
                    if (this.dealerSelectionUrl) {
                        setTimeout(() => {
                            window.location.href = this.dealerSelectionUrl;
                        }, 1500);
                    }
                }

                // Fetch updated cart to reflect shipping changes
                const updatedCart = await this.api.client.invoke("readCart get /checkout/cart");
                this.internalCart = updatedCart;

                // Update transaction ID for tracking
                if (updatedCart && updatedCart.token) {
                    localStorage.setItem("transactionId", updatedCart.token);
                }

                // Inform other components
                EventBus.$emit("cart-updated", updatedCart);

                // Track shipping info update for analytics
                this.trackShippingMethodChange(shippingMethodId);
            } catch (error) {
                console.error("Error updating shipping method:", error);
                EventBus.$emit('trigger-alert', {
                    message: this.$t('checkout.shipping_method_update_error'),
                    state: 'error'
                });

                // Reset to previous selection
                this.selectedShippingMethodId = this.cartData?.deliveries?.[0]?.shippingMethod?.id;
            } finally {
                this.isUpdatingShippingMethod = false;
                EventBus.$emit('updating-shipping-method', false);
            }
        },

        trackShippingMethodChange(shippingMethodId) {
            try {
                const selectedMethod = this.shippingMethods.find(m => m.id === shippingMethodId);
                if (!selectedMethod) return;

                const methodName = selectedMethod.translated?.name || selectedMethod.name || 'unknown';
                const shippingPrice = this.getShippingPrice(selectedMethod) || 0;

                window.dataLayer = window.dataLayer || [];
                window.dataLayer.push({
                    'event': 'add_shipping_info',
                    'shipping_tier': methodName,
                    'value': this.cartData?.price?.totalPrice || 0,
                    'shipping': shippingPrice,
                    'currency': 'EUR',
                    'items': this.getGTMItems()
                });
            } catch (error) {
                console.error("Error tracking shipping method change:", error);
                // Non-critical error, don't disrupt the user flow
            }
        },

        getGTMItems() {
            if (!this.cartData || !this.cartData.lineItems) return [];

            return this.cartData.lineItems.map((item, index) => ({
                item_id: item.payload?.productNumber || '',
                item_name: item.label,
                item_list_name: item.payload?.category?.name || (item.payload?.productNumber ? 'insectenbestrijders' : 'voucher'),
                item_brand: item.payload?.manufacturer?.name || "DCM",
                price: item.price?.unitPrice || 0,
                quantity: item.quantity || 1,
                index: index + 1,
                currency: 'EUR',
                item_category: item.payload?.category?.name || (item.payload?.productNumber ? 'insectenbestrijders' : 'voucher')
            }));
        }
    },
};
</script>

<style scoped>
.shipping-price {
    font-weight: bold;
    white-space: nowrap;
    margin-left: 1rem;
}

.shipping-price.free {
    color: #28a745;
}

.form-check {
    margin-bottom: 0.75rem;
    padding: 0.5rem;
    border-radius: 0.25rem;
    transition: background-color 0.2s ease;
}

.form-check:hover {
    background-color: rgba(0, 0, 0, 0.03);
}

.form-check-label {
    cursor: pointer;
    display: flex;
    width: 100%;
}
</style>
